import React, { useState } from "react";
import { TrendingUp, Activity, ShieldCheck, ArrowRight, Info, ChevronDown } from "lucide-react";
import { fmtRate } from "./api";

const RISK_CONFIG = {
  likely_up: {
    icon: TrendingUp,
    label: "Likely Up",
    bg: "bg-signal-up-bg",
    text: "text-signal-up",
    border: "border-signal-up/20",
  },
  volatile: {
    icon: Activity,
    label: "Volatile",
    bg: "bg-signal-volatile-bg",
    text: "text-signal-volatile",
    border: "border-signal-volatile/20",
  },
  stable: {
    icon: ShieldCheck,
    label: "Stable",
    bg: "bg-signal-stable-bg",
    text: "text-signal-stable",
    border: "border-signal-stable/20",
  },
};

// Neutral monochrome shield-with-maple-leaf used in place of a real lender
// logo. Keeps the cards trustworthy + Canadian without revealing the lender.
function MaskedLenderIcon({ size = 28 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="text-brand-navy/70"
    >
      {/* Shield outline */}
      <path
        d="M16 2.5 4.5 6.5v9.2c0 5.4 3.5 10.4 8.6 12.8L16 30l2.9-1.5c5.1-2.4 8.6-7.4 8.6-12.8V6.5L16 2.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      {/* Maple leaf in center */}
      <path
        d="M16 9.5 17 12l2.4-.6-.9 2.2 2.1 1.3-2.1 1 .6 2.5-2.6-.4-.5 2.4-1-1.6-1 1.6-.5-2.4-2.6.4.6-2.5-2.1-1 2.1-1.3-.9-2.2 2.4.6L16 9.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function SelectionExplainer({ selection, testid }) {
  const [open, setOpen] = useState(false);
  if (!selection) return null;

  const SOURCE_BADGE = {
    sheet: { label: "SHEET", className: "bg-slate-100 text-slate-700" },
    promo: { label: "PROMO", className: "bg-amber-100 text-amber-800" },
    buydown: { label: "BUY-DOWN", className: "bg-emerald-100 text-emerald-800" },
  };

  return (
    <div className="mb-4 -mt-2" data-testid={`${testid}-selection-explainer`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-explain-toggle`}
        className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-400 hover:text-brand-navy font-body transition-colors"
      >
        <Info size={12} strokeWidth={2.5} />
        How this rate was selected
        <ChevronDown
          size={12}
          strokeWidth={2.5}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-3" data-testid={`${testid}-explainer-panel`}>
          <p className="text-[12px] text-slate-700 leading-relaxed font-body">
            {selection.rationale}
          </p>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-body font-bold">
            Considered {selection.considered_count} candidate rates from {selection.lenders_considered} lenders
          </div>
          <div className="space-y-1.5">
            {(selection.candidates_top5 || []).map((c) => {
              const sb = SOURCE_BADGE[c.source] || SOURCE_BADGE.sheet;
              return (
                <div
                  key={`${c.rank}-${c.source}-${c.tier_label}-${c.product_category || ''}`}
                  className={`flex items-center gap-2 text-[12px] font-body py-1 px-2 rounded ${
                    c.is_winner ? "bg-white ring-1 ring-emerald-400" : ""
                  }`}
                  data-testid={`${testid}-candidate-${c.rank}`}
                >
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${sb.className}`}>
                    {sb.label}
                  </span>
                  <span className="font-bold tabular-nums text-brand-navy">{c.rate.toFixed(2)}%</span>
                  <span className="text-slate-500 truncate flex-1">
                    {c.tier_label}
                    {c.product_category && ` · ${c.product_category}`}
                  </span>
                  {c.source === "buydown" && (
                    <span className="text-[10px] text-emerald-700 font-medium tabular-nums">
                      −{c.buydown_bps}bps from {c.base_rate.toFixed(2)}%
                    </span>
                  )}
                  {c.is_winner && (
                    <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-600">
                      Winner
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-slate-400 font-body italic leading-relaxed">
            Lender names are masked on the public widget. We disclose them only after you start an inquiry.
          </div>
        </div>
      )}
    </div>
  );
}

export default function FeaturedRates({ data, loading, onInquire, onCompareAll }) {
  const cards = data?.cards || [
    { label: "5-Year Fixed", risk: "stable" },
    { label: "5-Year Variable", risk: "volatile" },
    { label: "3-Year Fixed", risk: "stable" },
  ];

  return (
    <section className="animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="featured-rates-section">
      <div className="mb-5 sm:mb-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display">
          Today's Featured Rates
        </h2>
        <p className="text-sm text-slate-500 font-body mt-1">
          Sourced live from {data?.lender_count || "50+"} Canadian lenders.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {cards.map((card, i) => (
          <FeaturedRateCard
            key={card.label}
            card={card}
            loading={loading}
            onInquire={() => onInquire?.(card)}
            delay={i * 80}
          />
        ))}
      </div>

      {onCompareAll && (
        <div className="mt-6 sm:mt-8 text-center">
          <button
            type="button"
            onClick={onCompareAll}
            data-testid="compare-all-link"
            className="group inline-flex items-center gap-2 text-brand-navy hover:text-brand-red text-sm sm:text-base font-bold font-body transition-colors"
          >
            <span className="underline underline-offset-4 decoration-1">Compare all rates</span>
            <span className="transition-transform group-hover:translate-x-1" aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </section>
  );
}

function FeaturedRateCard({ card, loading, onInquire, delay = 0 }) {
  const risk = RISK_CONFIG[card.risk] || RISK_CONFIG.stable;
  const RiskIcon = risk.icon;
  const slug = card.term?.replace(/\s+/g, "-") || card.label.toLowerCase().replace(/\s+/g, "-");
  const testid = `featured-rate-card-${slug}`;
  const tierLabel = card.tier_label || "Top Canadian Lender";
  const channelLabel = card.channel_label || "Broker Channel · Best Available Today";

  return (
    <article
      data-testid={testid}
      className="group relative bg-white border border-slate-200 rounded-2xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-brand-navy/20 animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Featured ribbon */}
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mb-4 font-body">
        — featured —
      </div>

      {/* Masked lender block (icon + 2-line tier/channel) */}
      <div className="flex items-start gap-3 mb-5" data-testid={`${testid}-masked-lender`}>
        <MaskedLenderIcon size={32} />
        <div className="min-w-0">
          <div
            className="text-sm font-bold text-brand-navy font-display leading-tight"
            data-testid={`${testid}-tier-label`}
          >
            {tierLabel}
          </div>
          <div
            className="text-[11px] text-slate-500 font-body leading-snug mt-0.5"
            data-testid={`${testid}-channel-label`}
          >
            {channelLabel}
          </div>
        </div>
      </div>

      {/* Big rate */}
      <div className="text-5xl sm:text-6xl font-bold tracking-tighter font-display tabular-nums text-brand-navy leading-none mb-2">
        {loading ? (
          <span className="inline-block w-32 h-12 bg-slate-100 rounded animate-pulse" />
        ) : (
          fmtRate(card.rate)
        )}
      </div>

      {/* Term sub-label */}
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 font-body mb-4">
        {card.label}
      </div>

      {/* Risk chip */}
      <div className={`inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest font-body border ${risk.bg} ${risk.text} ${risk.border} mb-6`}>
        <RiskIcon size={12} strokeWidth={2.5} />
        {risk.label}
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={onInquire}
        disabled={!card.available && !loading}
        data-testid={`${testid}-inquire`}
        className="mt-auto inline-flex items-center justify-between w-full bg-brand-navy text-white rounded-xl px-4 py-3 font-bold text-sm font-body group-hover:bg-brand-navy-hover transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
      >
        Inquire about this rate
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
      </button>
    </article>
  );
}
